// AuthContext.tsx
import React, {createContext, useCallback, useEffect} from 'react';
import {useLocalStorage} from "@rehooks/local-storage";
import {useCurrentAuthenticatedUserLazyQuery, useRefreshTokenMutation, UserRole} from "../GraphQL/generated/graphql";

export type ParsedAuthToken = {
    exp: number;
    iat: number;
    iss: string;
    jti: string;
    nbf: number;
    sub: string;
    "http://schemas.microsoft.com/ws/2008/06/identity/claims/role": string | string[];
    "UserId": string;
    "UserName": string;
}

export type AuthState = {
    token?: string;
    parsedToken?: ParsedAuthToken;
    roles: UserRole[];
    userName?: string;
    userId?: string;
};

const parseAuthToken = (token: string): ParsedAuthToken => JSON.parse(atob(token.split('.')[1])) as ParsedAuthToken;

const initialState: AuthState = {
    token: undefined,
    roles: [],
};

export const AuthContext = createContext<{
    auth: AuthState;
    setToken: (token: string) => void;
    signOut: () => void;
    refreshAndSetToken: () => void;
}>({
    auth: initialState,
    setToken: () => {
    },
    signOut: () => {
    },
    refreshAndSetToken: () => {
    }
});

export const AuthContextEntry = ({children}: { children: React.ReactNode }) => {
    const [auth, setAuth] = useLocalStorage<AuthState>("authState", initialState);
    const [refreshToken] = useRefreshTokenMutation();
    const [loadCurrentUser] = useCurrentAuthenticatedUserLazyQuery({
        fetchPolicy: "network-only"
    });

    const setToken = useCallback((token: string) => {
        if (token) {
            const parsedToken = parseAuthToken(token);
            const userName = parsedToken?.["UserName"];
            const userId = parsedToken?.["UserId"];
            let roleOrRoles = parsedToken?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
            if (!Array.isArray(roleOrRoles)) {
                roleOrRoles = [roleOrRoles];
            }
            const roles = roleOrRoles
                .map((role: string) => UserRole[role as keyof typeof UserRole])
            setAuth({token, parsedToken, roles, userName, userId});
        }
    }, []);

    const signOut = useCallback(() => {
        setAuth(initialState);
    }, []);
    
    const refreshAndSetToken = useCallback(async () => {
        try {
            const response = await refreshToken();
            const nextToken = response.data?.refreshToken?.token ?? "";
            if (!nextToken) {
                signOut();
                return;
            }

            setToken(nextToken);
        } catch (error) {
            console.error(error);
            signOut();
        }
    }, [refreshToken, setToken, signOut]);

    const validateAuthenticatedUser = useCallback(async () => {
        if (!auth.token) {
            return;
        }

        const currentUserResponse = await loadCurrentUser();
        const currentUser = currentUserResponse.data?.user?.[0];
        if (currentUser && (!auth.userId || auth.userId === currentUser.id)) {
            return;
        }

        try {
            const refreshResponse = await refreshToken();
            const nextToken = refreshResponse.data?.refreshToken?.token ?? "";
            if (!nextToken) {
                signOut();
                return;
            }

            const nextParsedToken = parseAuthToken(nextToken);
            setToken(nextToken);
            const refreshedUserResponse = await loadCurrentUser();
            const refreshedUser = refreshedUserResponse.data?.user?.[0];
            if (!refreshedUser || nextParsedToken?.["UserId"] !== refreshedUser.id) {
                signOut();
            }
        } catch (error) {
            console.error(error);
            signOut();
        }
    }, [auth.token, loadCurrentUser, refreshToken, setToken, signOut]);

    useEffect(() => {
        const authStateFromStorage = localStorage.getItem("authState");
        if (authStateFromStorage) {
            const tokenFromStorage = JSON.parse(authStateFromStorage).token;
            if (tokenFromStorage) {
                const parsedToken = JSON.parse(atob(tokenFromStorage.split('.')[1]));
                const issueTime = new Date(parsedToken.iat * 1000);
                const expirationTime = new Date(parsedToken.exp * 1000);

                // Calculate total validity duration in milliseconds
                const totalValidityDuration = expirationTime.getTime() - issueTime.getTime();

                // Calculate the 75% threshold
                const refreshThreshold = new Date(issueTime.getTime() + (totalValidityDuration * 0.75));

                // If current time is beyond the 75% threshold, refresh the token
                if (new Date() > refreshThreshold) {
                    refreshAndSetToken();
                }else if (new Date() > expirationTime) {
                    signOut();
                }
            }
        }
    }, [refreshAndSetToken, signOut]);

    useEffect(() => {
        validateAuthenticatedUser().catch(error => {
            console.error(error);
            signOut();
        });
    }, [signOut, validateAuthenticatedUser]);

    return <AuthContext.Provider value={{auth, setToken, signOut, refreshAndSetToken}}>
        {children}
    </AuthContext.Provider>;
}
